import {
  getIconSVG,
  PplIconService,
  svgToDataURL
} from './icon.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input
} from '@angular/core';
import { Identity } from '@ppl/environment';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import type { Subscription } from 'rxjs';

export interface PplIconState {
  name: string;
  active: boolean;
}

const HOVER_VALUE = 'hover';
const STATE_DELIMITER = '_';

@Component({
  selector: 'ppl-icon',
  template: '',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PplIconComponent implements OnInit, OnChanges, OnDestroy {
  @Input() svgIcon: string;
  @Input() hoverIcon: string;
  @Input() actionElement: Element;
  @Input() hoverEnabled = false;
  @Input() states: PplIconState[] = [];
  @Input() useSvg = false;
  @Input() svgStrip: { enabled: boolean, keepEmptyFill?: boolean; } = null;
  @Input() @HostBinding('style.--ppl-icon-color') color?: string;
  @Input() fill?: string;
  @Input() hoverFill?: string;

  @Input() width = '20px';
  @Input() height = '20px';
  @Input() legacyWidth?: string;
  @Input() legacyHeight?: string;

  private element: Element;
  private isHovered: boolean;
  private iconsLoadedSubscription: Subscription;

  private mouseEnterHandler: EventListener;
  private mouseLeaveHandler: EventListener;

  @MemoizeLast<PplIconComponent>(['svgIcon'])
  get isLucideIcon() {
    const resultIcon = this.iconService.icons[this.svgIcon];

    return resultIcon && resultIcon.innerHTML.includes('data-source="lucide"');
  }

  @HostBinding('style.fill')
  get fillValue() {
    if (this.useSvg && Identity === 'pipeliner') {
      return this.fill;
    }
  }

  @HostBinding('style.width')
  get widthValue() {
    if (this.legacyWidth && !this.isLucideIcon) {
      return this.legacyWidth;
    }

    return this.width;
  }

  @HostBinding('style.height')
  get heightValue() {
    if (this.legacyHeight && !this.isLucideIcon) {
      return this.legacyHeight;
    }

    return this.height;
  }

  constructor(public elementRef: ElementRef, private iconService: PplIconService, private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['svgIcon'] && !changes['svgIcon'].isFirstChange() && changes['svgIcon'].previousValue !== changes['svgIcon'].currentValue) ||
      (changes['fill'] && !changes['fill'].isFirstChange() && changes['fill'].previousValue !== changes['fill'].currentValue) ||
      (changes['states'] && this.hasStateChanged(changes['states'].previousValue, changes['states'].currentValue))
    ) {
      this.initStates();
    }

    if (changes.hoverEnabled && !changes.hoverEnabled.firstChange && changes.hoverEnabled.currentValue !== changes.hoverEnabled.previousValue) {
      if (changes.hoverEnabled.currentValue) {
        this.registerHoverListeners();
      } else {
        this.unregisterHoverListeners();
      }
    }
  }

  ngOnInit() {
    if (!this.iconService.isLoaded$.getValue()) {
      this.iconsLoadedSubscription = this.iconService.isLoaded$.subscribe(() => {
        this.initStates();
      });
    }

    this.element = this.actionElement || this.elementRef.nativeElement;
    this.initStates();

    if (this.hoverEnabled) {
      this.registerHoverListeners();
    }
  }

  ngOnDestroy() {
    if (this.hoverEnabled) {
      this.unregisterHoverListeners();
    }
    if (this.iconsLoadedSubscription) {
      this.iconsLoadedSubscription.unsubscribe();
    }
  }

  registerHoverListeners() {
    this.element.addEventListener('mouseenter', this.mouseEnterHandler = this.onMouseEnter.bind(this));
    this.element.addEventListener('mouseleave', this.mouseLeaveHandler = this.onMouseLeave.bind(this));
  }

  unregisterHoverListeners() {
    this.element.removeEventListener('mouseenter', this.mouseEnterHandler);
    this.element.removeEventListener('mouseleave', this.mouseLeaveHandler);
  }

  onMouseEnter() {
    this.isHovered = true;
    this.initStates();
  }

  onMouseLeave() {
    this.isHovered = false;
    this.initStates();
  }

  private hasStateChanged(previousValue: PplIconState[] | null, currentValue: PplIconState[] | null) {
    if (!currentValue || !previousValue) {
      return true;
    } else {
      return !!currentValue.find((state, index) => previousValue[index].name !== state.name || previousValue[index].active !== state.active);
    }
  }

  private initStates() {
    if (this.element) {
      let iconName = this.svgIcon;
      const iconStates = new Set();

      this.states.forEach((state) => {
        if (state.active) {
          iconStates.add(state.name);
        }
      });

      if (this.isHovered && !iconStates.has(HOVER_VALUE) && !this.hoverIcon) {
        iconStates.add(HOVER_VALUE);
      } else if (this.isHovered && this.hoverIcon) {
        iconName = this.hoverIcon;
      }

      if (iconStates.size > 0) {
        iconName = iconName + STATE_DELIMITER + Array.from(iconStates).join(STATE_DELIMITER);
      }

      const baseIcon = this.iconService.icons[this.svgIcon];
      const resultIcon = this.iconService.icons[iconName];
      const icon = resultIcon ? resultIcon : baseIcon ? baseIcon : null;

      if (icon) {
        if (!this.useSvg) {
          let url = icon.dataURL;

          if (icon.innerHTML.includes('data-source="lucide"')) {
            const defaultColor = icon.innerHTML.match(/data-default-color="(.*?)"/)?.[1] || 'var(--ppl-color-500, #005895)';

            const activeFill = this.isHovered ? (this.hoverFill || this.fill) : this.fill;
            let background = activeFill || defaultColor;

            this.elementRef.nativeElement.style.mask = `url(${url}) no-repeat center`;
            this.elementRef.nativeElement.style.background = background;
          } else {
            this.elementRef.nativeElement.style.mask = '';
            this.elementRef.nativeElement.style.background = `url(${url}) no-repeat center`;
          }
          return;
        }

        this.elementRef.nativeElement.innerHTML = !!this.svgStrip?.enabled
          ? getIconSVG(icon.innerHTML, this.svgStrip.keepEmptyFill)
          : icon.innerHTML;
      }
    }
  }
}
